import * as yup from 'yup'
import { emailRegex } from './util'

export const userProfileSchema = yup.object().shape({
  isFirstTimeUser: yup.boolean(),
  promptToComplete: yup.boolean(),
  isSSO: yup.boolean(),
  country: yup.string().when('promptToComplete', {
    is: promptToComplete => promptToComplete === false,
    then: yup.string().required('Required'),
  }),
  city: yup.string().when('promptToComplete', {
    is: promptToComplete => promptToComplete === false,
    then: yup.string().required('Required'),
  }),
  state: yup.string(),
  displayName: yup.string(),
  email: yup.string().when(['isFirstTimeUser', 'promptToComplete'], {
    is: (isFirstTimeUser, promptToComplete) =>
      !isFirstTimeUser && !promptToComplete,
    then: yup
      .string()
      .email('Enter a valid Email Address')
      .required('Enter your Email Address'),
  }),
  firstName: yup
    .string()
    .when(['isFirstTimeUser', 'promptToComplete', 'isSSO'], {
      is: (isFirstTimeUser, promptToComplete, isSSO) =>
        !isFirstTimeUser && !promptToComplete && !isSSO,
      then: yup
        .string()
        .max(50, "Enter a First Name that's 50 characters or fewer")
        .required('Enter your First Name'),
      otherwise: yup.string().notRequired().nullable(),
    }),
  lastName: yup
    .string()
    .when(['isFirstTimeUser', 'promptToComplete', 'isSSO'], {
      is: (isFirstTimeUser, promptToComplete, isSSO) =>
        !isFirstTimeUser && !promptToComplete && !isSSO,
      then: yup
        .string()
        .max(50, "Enter a Last Name that's 50 characters or fewer")
        .required('Enter your Last Name'),
      otherwise: yup.string().notRequired().nullable(),
    }),
  title: yup.string().required('Required').nullable(),
  netsuiteCustomerId: yup.lazy(value =>
    typeof value === 'number'
      ? yup.number().nullable()
      : yup.string().nullable(),
  ),
  companyName: yup.string().when(['netsuiteCustomerId', 'promptToComplete'], {
    is: (netsuiteCustomerId, promptToComplete) =>
      netsuiteCustomerId !== 0 && !promptToComplete,
    then: yup.string().required('Required'),
    otherwise: yup.string().nullable(),
  }),
  archetypes: yup.array(yup.number()).required('Required'),
})

export const licenseSchema = yup.object().shape({
  // License-specific fields
  isCanceled: yup.boolean(),
  cancelledDate: yup.date().notRequired().nullable(),
  siteWasChanged: yup.boolean(),
  siteChangeChecked: yup.boolean().when('siteWasChanged', {
    is: true,
    then: yup.boolean().oneOf([true], 'Required'),
    otherwise: yup.boolean().default(false),
  }),

  // Site fields (flattened structure)
  nsCustomerId: yup.string().nullable(),
  netsuiteCustomerId: yup.lazy(value =>
    typeof value === 'number'
      ? yup.number().nullable()
      : yup.string().nullable(),
  ),
  city: yup.string().when('nsCustomerId', {
    is: nsCustomerId =>
      nsCustomerId !== '' &&
      nsCustomerId !== undefined &&
      nsCustomerId !== null,
    then: () => yup.string().nullable(),
    otherwise: () => yup.string().required('City is required'),
  }),
  country: yup.string().when('nsCustomerId', {
    is: nsCustomerId =>
      nsCustomerId !== '' &&
      nsCustomerId !== undefined &&
      nsCustomerId !== null,
    then: () => yup.string().nullable(),
    otherwise: () => yup.string().required('Country is required'),
  }),
  region: yup.string().nullable(),
  companyName: yup.string().nullable(),
  organizationName: yup.string().when(['nsCustomerId', 'netsuiteCustomerId'], {
    is: (nsCustomerId, netsuiteCustomerId) =>
      !nsCustomerId && !netsuiteCustomerId,
    then: yup
      .string()
      .required(
        'Organization name is required when no customer ID is provided',
      ),
    otherwise: yup.string().nullable(),
  }),
})

export const editSiteSchema = yup.object().shape({
  city: yup
    .string()
    .required()
    .ensure()
    .when('nsCustomerId', {
      is: nsCustomerId =>
        nsCustomerId !== '' &&
        nsCustomerId !== undefined &&
        nsCustomerId !== null,
      then: () => yup.string().nullable(),
      otherwise: () => yup.string().required(),
    }),
  companyName: yup.string(),
  country: yup.string().required(),
  netsuiteCustomerId: yup.number().nullable(),
  nsCustomerId: yup.string().nullable(),
  organizationId: yup.number().required(),
  organizationName: yup.string().nullable(),
  region: yup.string().nullable(),
})

export const SSOToolSchema = yup.object().shape({
  provider: yup.string().required('Please, choose the proper Provider'),
  displayName: yup.string().required(['Display Name can not be null']),
  domains: yup
    .string()
    .lowercase()
    .test(
      'domains',
      'domains must be in correct email domain format',
      function (value) {
        const domains = value
          ? value
              .split(',')
              .map(domain => domain.trim())
              .filter(domain => !!domain)
          : []

        domains.forEach(domain => {
          const domainRegex = /^(?:[a-zA-Z0-9-]{2,}\.)*[a-zA-Z0-9]{2,6}$/
          if (!domainRegex.test(domain)) {
            throw this.createError({
              message: `Domains must be in the correct format`,
            })
          }
        })

        return true
      },
    )
    .trim()
    .required('Insert at least one domain')
    .strict(),
  activeDate: yup.date().required('Active Date can not be null'),
})

export const customerMergeSchema = yup.object().shape({
  activePrimaryId: yup
    .string()
    .oneOf(['primaryNSCustomerId', 'primarySecondstepId']),
  primaryNSCustomerId: yup.number().when('activePrimaryId', {
    is: activePrimaryId => activePrimaryId === 'primaryNSCustomerId',
    then: yup
      .number()
      .required()
      .notOneOf(
        [yup.ref('duplicateNSCustomerId')],
        'Netsuite ids must be different',
      ),
    otherwise: yup.number(),
  }),
  primarySecondstepId: yup.number().when('activePrimaryId', {
    is: activePrimaryId => activePrimaryId === 'primarySecondstepId',
    then: yup
      .number()
      .required()
      .notOneOf(
        [yup.ref('duplicateSecondstepId')],
        'Secondstep ids must be different',
      ),
    otherwise: yup.number(),
  }),

  activeDuplicateId: yup
    .string()
    .oneOf(['duplicateNSCustomerId', 'duplicateSecondstepId']),
  duplicateNSCustomerId: yup.number().when('activeDuplicateId', {
    is: activeDuplicateId => activeDuplicateId === 'duplicateNSCustomerId',
    then: yup.number().required(),
    otherwise: yup.number(),
  }),
  duplicateSecondstepId: yup.number().when('activeDuplicateId', {
    is: activeDuplicateId => activeDuplicateId === 'duplicateSecondstepId',
    then: yup.number().required(),
    otherwise: yup.number(),
  }),
})

export const validateEmail = value => {
  if (!emailRegex.test(value)) {
    throw new yup.ValidationError('Please enter only one valid email address')
  }
  return true
}

export const mergeSiteSchema = yup.object().shape({
  siteId: yup.number().required(),
  primarySiteId: yup.number().required(),
})

export const singleAddUserSchema = yup.object().shape({
  selectedRole: yup.string().required(),
  sendNotification: yup.string().required(),
  emails: yup
    .string()
    .max(256, 'Please enter 256 characters or less')
    .trim()
    .required('Please enter an email address')
    .matches(emailRegex, { message: 'Invalid email address' }),
  otherwise: yup.string(),
})

export const bulkAddUserSchema = yup.object().shape({
  selectedRole: yup.string().required(),
  sendNotification: yup.string().required(),
  emails: yup
    .string()
    .required('Please enter at least one email address')
    .test('You have exceeded the maximum number of email addresses', value => {
      const MAX_EMAILS = 200
      const emails = value
        ? value
            .split(/,|;/)
            .map(email => email.trim())
            .filter(email => !!email)
        : []

      if (emails.length > MAX_EMAILS) {
        throw this.createError({
          message: `You've exceeded the maximum number of email addresses (${MAX_EMAILS})`,
        })
      }

      return true
    }),
})

export const deleteUserSchema = yup.object().shape({
  userEmail: yup.string().label('User Email'),
})
